import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { FaPlay, FaTimes, FaVideo } from 'react-icons/fa';
import './VideoGallery.css';

// Importing the image directly for the src tag
import img from '../../assets/breadcrumb/videos-Gallery.jpg'; 

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper to extract ID from various YouTube URL formats
const getYoutubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// Helper to get High Res thumbnail
const getThumbnail = (url) => {
  const id = getYoutubeId(url);
  // Using mqdefault as it is the most reliable fallback, or maxresdefault if available
  return id 
    ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` 
    : "https://via.placeholder.com/800x450?text=Video+Unavailable";
};

function VideoGallery() {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoData, setVideoData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch Videos
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/ytVideos/public`);
        const data = await response.json();
        setVideoData(Array.isArray(data) ? data : []); 
      } catch (error) {
        console.error("Failed to load videos", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [selectedVideo]);

  const handleOpenVideo = (video) => setSelectedVideo(video);
  const handleCloseVideo = () => setSelectedVideo(null);

  const selectedVideoId = selectedVideo ? getYoutubeId(selectedVideo.link) : null;

  return (
    <>
    <Helmet>
      <title>Video Gallery | SK College</title>
      <meta name="description" content="Explore video moments, campus events, and student activities at SK College." />
      <meta name="keywords" content="SK College Videos, College Events, Campus Life, Video Gallery" />
      <link rel="canonical" href="https://skcollegemanaklao.com/video-gallery" />
    </Helmet>

    <div className="video-of-sk-college-wrapper">
      
      {/* --- HEADER SECTION --- */}
      <div className="video-of-sk-college-header-container">
        <img src={img} alt="SK College Video Gallery" className="video-of-sk-college-header-img" />
        <div className="video-of-sk-college-header-overlay">
          <h1 className="video-of-sk-college-title">Moments @ SK College</h1>
          <div className="video-of-sk-college-breadcrumb">
            <span className="video-of-sk-college-bread-static">Home</span>
            <span className="video-of-sk-college-sep">/</span>
            <span className="video-of-sk-college-bread-active">Video Gallery</span>
          </div>
        </div>
      </div>

      <div className="video-of-sk-college-content">
        
        {/* Intro Text */}
        <div className="video-of-sk-college-intro">
          <h2 className="video-of-sk-college-section-title">Visual Stories</h2>
          <p className="video-of-sk-college-section-subtitle">
            Experience the vibrant life, achievements, and events through our lens.
          </p>
        </div>

        {/* Video Grid */}
        <div className="video-of-sk-college-grid">
          {loading ? (
            <div className="video-of-sk-college-loading">Loading Gallery...</div>
          ) : videoData.length === 0 ? (
            <div className="video-of-sk-college-empty">No videos found.</div>
          ) : (
            videoData.map((video) => (
              <div 
                key={video._id || Math.random()} 
                className="video-of-sk-college-card"
                onClick={() => handleOpenVideo(video)}
              >
                <div className="video-of-sk-college-thumb-wrapper">
                  <img src={getThumbnail(video.link)} alt={video.title} loading="lazy" />
                  <div className="video-of-sk-college-play-overlay">
                    <div className="video-of-sk-college-play-btn">
                      <FaPlay />
                    </div>
                  </div>
                </div>
                <div className="video-of-sk-college-card-info">
                  <h3 className="video-of-sk-college-card-title">
                    <FaVideo className="video-of-sk-college-icon" /> 
                    <span>{video.title}</span>
                  </h3>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedVideo && selectedVideoId && (
        <div className="video-of-sk-college-lightbox" onClick={handleCloseVideo}>
          <div className="video-of-sk-college-lightbox-content" onClick={(e) => e.stopPropagation()}>
            
            <div className="video-of-sk-college-lightbox-video-container">
              {/* Close Button is now absolutely positioned relative to this container */}
              <button className="video-of-sk-college-close-btn" onClick={handleCloseVideo}>
                <FaTimes />
              </button>
              
              <iframe 
                src={`https://www.youtube.com/embed/${selectedVideoId}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>

            <div className="video-of-sk-college-lightbox-desc">
              {selectedVideo.title}
            </div>
          </div>
        </div>
      )}

    </div>
    </>
  );
}

export default VideoGallery;